import * as d3 from "d3"
import moment from "moment"
export type TimetableData<T> = {
    start: Date,
    end: Date
    betweenTextcolor?: string
    color: string
} & T

export type TimetableOption<T> = {
    margin?: {
        left?: number,
        right?: number,
        top?: number,
        bottom?: number
    }
    circleStrokeWidth?: number
    hourText?: {
        outerFromDistance?: number
    }
}
const limitCorrection = (numb: number) => {
    const a = String(numb);
    const b = a.split("")
    const c = b.findIndex(v => v === "e")

    if (c > 0) {
        return 0
    } else {
        return numb
    }
}
// SunBurstChart
export default function pieTimetable<T>(node: HTMLElement, data: TimetableData<T>[], option: TimetableOption<T>) {
    // Compute values.
    const {
        margin,
        circleStrokeWidth = 2,
        hourText
    } = option;
    const { left = 0, right = 0, top = 0, bottom = 0 } = margin || {};
    const { width, height } = node.getBoundingClientRect();
    const {
        outerFromDistance = 5
    } = hourText || {};
    const mappedData = data.map((v) => {
        const { color, betweenTextcolor } = v;
        const start = (v.start.getHours() * 60) + v.start.getMinutes();
        const end = (v.end.getHours() * 60) + v.end.getMinutes();
        return {
            start,
            end,
            color,
            betweenTextcolor
        }
    })
    // SVG
    const svg = d3.create("svg")
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", `0, 0, ${width}, ${height}`)
        .attr("style", "max-width: 100%");

    //Table Circle Blur 
    const filter = svg
        .append("defs")
        .append("filter")
        .attr("id", "blur-1")
        .attr("x", -0.1)
        .attr("y", -0.1)

    filter.append("feGaussianBlur")
        .attr("stdDeviation", 2)
        .attr("result", "blur")

    filter.append("feFlood")
        .attr("flood-opacity", 0.1)

    filter.append("feComposite")
        .attr("operator", "in")
        .attr("in2", "blur")

    filter.append("feComposite")
        .attr("in", "SourceGraphic")

    const pieWidth = width - left - right;
    const pieHeight = height - top - bottom;
    const maxOuterRadiuse = Math.min(pieWidth, pieHeight) / 2;

    // 
    const minuteInHour = 60;
    const totalHour = 24;
    const timeDivisionOperand = 24;
    const numbOfDivision = totalHour / timeDivisionOperand;
    const timeOffsets = [];

    for (let i = numbOfDivision; i <= totalHour; i = i + numbOfDivision) {
        timeOffsets.push(numbOfDivision * minuteInHour)
    }

    // Render table
    const hourCircleRadius = maxOuterRadiuse + outerFromDistance
    const tableGroup = svg.append('g')
        .attr('transform', `translate(${width / 2}, ${height / 2})`)

    const arcs = d3.pie().sort(null).padAngle(0)(timeOffsets);


    //Table Circle
    tableGroup
        .append('circle')
        .attr("r", maxOuterRadiuse - (circleStrokeWidth / 2))
        .attr("fill", "white")
        .attr("stroke-width", circleStrokeWidth)
        .attr("stroke", "transparents")
        .attr("filter", "url(#blur-1)")


    //Circle Texts
    svg
        .append("g")
        .attr("id", 'hourlabel-group')
        .attr('transform', `translate(${width / 2}, ${height / 2})`)
        .selectAll("text")
        .data(arcs)
        .join("text")
        .attr("transform", (d, i) => {
            // 기울기 A가 주어졌을때, 반지름 X에 해당하는 거리의 좌표는 
            //P = [Cos(A),sin(A)] 이지만 d3는 90도의 각도를 0으로 보기 때문에, 이를 반전 해야 한다.
            //또한 평면 좌표도에서는 절대 좌표 이지만, 그래픽은 상대적인 좌표계이기 때문에 이를 보완 하기 위해 * -1을 해줘야 보정이된다.
            const newX: number = hourCircleRadius * Math.sin(d.startAngle)
            const newY = hourCircleRadius * Math.cos(d.startAngle)
            const newA = [
                limitCorrection(newX),
                limitCorrection(newY * -1),

            ]
            return `translate(${newA})`
        })
        .attr("class", "hour-label")
        .attr("id", (_, i) => "hour-label-" + i)
        .text((_, i) => {
            if (i % 2 !== 0) {
                return "."
            }

            const prefix = i >= 12 ? "오후 " : "오전 ";
            const time = (i % 12) === 0 ? 12 : i % 12
            return prefix + time;
        })
        .attr("text-anchor", (d, i) => {
            const newX = limitCorrection(hourCircleRadius * Math.sin(d.startAngle))
            if (newX === 0) {
                return "middle"
            } else if (newX > 0) {
                return "start"
            } else {
                return "end"
            }
        })
        .attr("alignment-baseline", (d, i) => {
            const newY = limitCorrection(hourCircleRadius * Math.cos(d.startAngle))
            if (newY === 0) {
                return "central"
            } else if (newY > 0) {
                return "text-bottom"

            } else {
                return "hanging"
            }
        })
        .style("fill", (_, i) => {
            const time = i * 60;
            const betweenInData = mappedData
                .find(v => (v.start <= time) && (time <= v.end))

            if (betweenInData) {
                return betweenInData.betweenTextcolor || ""
            } else {
                return ""
            }
        })

    // Data Pie 
    const timeRange = [
        0,
        24 * 60
    ]

    const arcRange = [
        0,
        arcs[arcs.length - 1].endAngle
    ]

    const pieScale = d3.scaleLinear(timeRange, arcRange);
    let i = 0;
    for (const data of mappedData) {
        const arc = d3.arc()
            .innerRadius(0)
            .outerRadius(maxOuterRadiuse)
            .startAngle(pieScale(data.start))
            .endAngle(pieScale(data.end));

        svg
            .append("g")
            .attr("id", `pie-group-${i}`)
            .attr('transform', `translate(${width / 2}, ${height / 2})`)
            .selectAll("path")
            .data([data])
            .join("path")
            .attr("id", `pie-${i}`)
            .attr("d", arc)
            .attr("fill", data.color)
        i++
    }

    node.appendChild(svg.node() as SVGSVGElement)
    let timeout: any;
    const resizeEvent = () => {
        const { width, height } = node.getBoundingClientRect();
        const pieWidth = width - left - right;
        const pieHeight = height - top - bottom;
        const maxOuterRadiuse = Math.min(pieWidth, pieHeight) / 2;
        const hourCircleRadius = maxOuterRadiuse + outerFromDistance
        // SVG
        if (timeout) {
            clearTimeout(timeout)
        }
        timeout = setTimeout(() => {
            svg
                .attr('width', width)
                .attr('height', height)
                .attr("viewBox", `0, 0, ${width}, ${height}`);
            // Table Group 
            tableGroup
                .attr('transform', `translate(${width / 2}, ${height / 2})`);
            // Circle
            tableGroup
                .select("circle")
                .attr("r", maxOuterRadiuse - (circleStrokeWidth / 2))
            // TextGroup
            svg
                .select("g#hourlabel-group")
                .attr('transform', `translate(${width / 2}, ${height / 2})`)
            // Text
            svg
                .selectAll("text.hour-label")
                .data(arcs)
                .attr("transform", (d, i) => {
                    // 기울기 A가 주어졌을때, 반지름 X에 해당하는 거리의 좌표는 
                    //P = [Cos(A),sin(A)] 이지만 d3는 90도의 각도를 0으로 보기 때문에, 이를 반전 해야 한다.
                    //또한 평면 좌표도에서는 절대 좌표 이지만, 그래픽은 상대적인 좌표계이기 때문에 이를 보완 하기 위해 * -1을 해줘야 보정이된다.

                    const newX: number = hourCircleRadius * Math.sin(d.startAngle)
                    const newY = hourCircleRadius * Math.cos(d.startAngle)
                    const newA = [
                        limitCorrection(newX),
                        limitCorrection(newY * -1),

                    ]
                    return `translate(${newA})`
                })
            let i = 0;
            for (const data of mappedData) {
                const arc = d3.arc<TimetableData<T>[]>()
                    .innerRadius(0)
                    .outerRadius(maxOuterRadiuse)
                    .startAngle(pieScale(data.start))
                    .endAngle(pieScale(data.end));

                svg
                    .select(`g#pie-group-${i}`)
                    .attr('transform', `translate(${width / 2}, ${height / 2})`)
                    .select(`path#pie-${i}`)
                    .data([data])
                    .attr("d", arc)
                i++
            }

        }, 100)

    }
    window.addEventListener("resize", resizeEvent)

    return {
        node: svg.node() as SVGSVGElement,
        resizeEvent
    }
}